import Bugsnag from "@bugsnag/js";
import type { PassportInfo } from "@trex-2b/api/src/trex-api/trex-api";

export const ACCESS_TOKEN_COOKIE_KEY = "trex2b_access_token";
export const REFRESH_TOKEN_COOKIE_KEY = "trex2b_refresh_token";
export const LOGIN_STRATEGY_STORAGE_KEY = "trex2b_login_strategy";
export const PASSPORT_INFO_STORAGE_KEY = "trex2b_passport_info";
/** Fired on `window` after stored login tokens are saved or cleared (same tab). */
export const TREX_STORED_LOGIN_CHANGED_EVENT = "trex-stored-login-changed";
const TREX_LOGOUT_SYNC_STORAGE_KEY = "trex2b_logout_sync";

const COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

const notifyStoredLoginChanged = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(TREX_STORED_LOGIN_CHANGED_EVENT));
};

const notifyCrossTabLogout = () => {
  if (!isBrowser()) return;
  window.localStorage.setItem(
    TREX_LOGOUT_SYNC_STORAGE_KEY,
    JSON.stringify({ timestamp: Date.now() }),
  );
};

export type LoginTokens = {
  accessToken: string;
  refreshToken: string;
};

type TClearLoginTokensOptions = {
  broadcastLogout?: boolean;
};

const isBrowser = () => typeof document !== "undefined";

const getCookie = (name: string): string | null => {
  if (!isBrowser()) return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() ?? null;
  }
  return null;
};

const setCookie = (
  name: string,
  value: string,
  options: {
    path?: string;
    maxAge?: number;
  } = {},
) => {
  if (!isBrowser()) return;

  let cookieString = `${name}=${encodeURIComponent(value)}`;
  if (options.path) {
    cookieString += `; path=${options.path}`;
  }
  if (options.maxAge) {
    cookieString += `; max-age=${options.maxAge}`;
  }

  document.cookie = cookieString;
};

const deleteCookie = (name: string, path = "/") => {
  if (!isBrowser()) return;
  document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    if (!payload.exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp <= now;
  } catch {
    return true;
  }
};

export const saveLoginTokens = ({ accessToken, refreshToken }: LoginTokens) => {
  if (!isBrowser()) return;

  saveLoginTokenCookies({ accessToken, refreshToken });
  notifyStoredLoginChanged();
};

export const saveLoginTokenCookies = ({
  accessToken,
  refreshToken,
}: LoginTokens) => {
  if (!isBrowser()) return;

  setCookie(ACCESS_TOKEN_COOKIE_KEY, accessToken, {
    path: "/",
    maxAge: COOKIE_MAX_AGE_SECONDS,
  });
  setCookie(REFRESH_TOKEN_COOKIE_KEY, refreshToken, {
    path: "/",
    maxAge: COOKIE_MAX_AGE_SECONDS,
  });
};

export const getStoredLoginTokens = (): LoginTokens | null => {
  if (!isBrowser()) return null;

  const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
  const refreshToken = getCookie(REFRESH_TOKEN_COOKIE_KEY);
  if (accessToken && refreshToken) {
    return { accessToken, refreshToken };
  }

  return null;
};

export const clearLoginTokens = (
  options: TClearLoginTokensOptions = { broadcastLogout: true },
) => {
  if (!isBrowser()) return;

  deleteCookie(ACCESS_TOKEN_COOKIE_KEY, "/");
  deleteCookie(REFRESH_TOKEN_COOKIE_KEY, "/");
  notifyStoredLoginChanged();
  if (options.broadcastLogout !== false) {
    notifyCrossTabLogout();
  }
};

export const subscribeCrossTabLogout = (onLogout: () => void) => {
  if (!isBrowser()) return () => {};

  const handleStorage = (event: StorageEvent) => {
    if (
      event.key === TREX_LOGOUT_SYNC_STORAGE_KEY &&
      event.newValue &&
      event.newValue !== event.oldValue
    ) {
      onLogout();
    }
  };

  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener("storage", handleStorage);
  };
};

export const savePassportInfo = (passportInfo: PassportInfo) => {
  if (!isBrowser()) return;
  window.localStorage.setItem(
    PASSPORT_INFO_STORAGE_KEY,
    JSON.stringify(passportInfo),
  );
  Bugsnag.setUser(
    passportInfo.passportId,
    passportInfo.email,
    passportInfo.handleName,
  );
};

export const getStoredPassportInfo = (): PassportInfo | null => {
  if (!isBrowser()) return null;

  const raw = window.localStorage.getItem(PASSPORT_INFO_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as PassportInfo;
  } catch {
    return null;
  }
};

export const clearPassportInfo = () => {
  if (!isBrowser()) return;
  window.localStorage.removeItem(PASSPORT_INFO_STORAGE_KEY);
};

/**
 * 获取 access token
 * @returns access token 或 null
 */
export const getAccessToken = (): string | null => {
  const token = getCookie(ACCESS_TOKEN_COOKIE_KEY);
  if (!token || isTokenExpired(token)) return null;
  return token;
};

/**
 * 获取 refresh token（注意：refresh token 通常是 httpOnly，客户端无法直接读取）
 * @returns refresh token 或 null
 */
export const getRefreshToken = (): string | null => {
  const token = getCookie(REFRESH_TOKEN_COOKIE_KEY);
  if (!token || isTokenExpired(token)) return null;
  return token;
};

/**
 * 生成按当前登录账号（passportId）区分的 React Query queryKey：`[scope, key, passportId]`。
 * 未登录时 passportId 为 null。用于避免持久化缓存跨账号串号：
 * 同一浏览器切换登录账号后，不会先读到上一个账号的缓存数据。
 *
 * @example
 * const generateAegisMyProjectQueryKey = () =>
 *   generatePassportQueryKey("aegis", "my-project");
 * generatePassportQueryKey("campaign", "cohorts", actionTemplateName);
 */
export const generatePassportQueryKey = (
  scope: string,
  key: string,
  ...segments: readonly string[]
) =>
  [
    scope,
    key,
    ...segments,
    getStoredPassportInfo()?.passportId ?? null,
  ] as const;
